import { Injectable, OnDestroy } from '@angular/core';
import { Metadata } from '@classifieds-sell/datalayer';
import { BehaviorSubject, combineLatest, Observable, of, Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { UserFacade, MotorsDraftFacade } from '@classifieds-sell/state';
import { FeeCalculationFacade } from '../state/fee-calculation/fee-calculation.facade';

@Injectable({
    providedIn: 'root'
})
export class FeeService implements OnDestroy {
    private _destroyed$: Subject<void> = new Subject<void>();
    topUpRequiredAmountChange$: Subject<number> = new Subject<number>();
    isTopUpRequired$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
    metadata: Metadata;
    private _listingCost: number;
    private _memberBalance: number;
    private _minimumTopUpAmount: number;
    private _topUpRequiredAmount: number;

    constructor(
        private _draftFacade: MotorsDraftFacade,
        private _userFacade: UserFacade,
        private _feeCalculationFacade: FeeCalculationFacade
    ) {
        combineLatest([this._draftFacade.state$, this._feeCalculationFacade.totalFee$])
            .pipe(takeUntil(this._destroyed$))
            .subscribe(([state, totalFee]) => {
                const categoryPackageId =
                    state.draftData?.listingDetails?.categoryPackageId;
                if (state.draftLoaded && categoryPackageId) {
                    this._listingCost = totalFee;
                }
                this.setAmountToTopUp();
            });

        combineLatest([
            this._userFacade.memberSummary$,
            this._userFacade.topUpAccountStatus$
        ])
            .pipe(takeUntil(this._destroyed$))
            .subscribe(([summary, topUpAccountStatus]) => {
                if (summary) {
                    this._memberBalance = summary.balance;
                }
                if (topUpAccountStatus) {
                    this._minimumTopUpAmount =
                        topUpAccountStatus.minimumTopUpAmount;
                }
                this.setAmountToTopUp();
            });

        this.topUpRequiredAmountChange$.subscribe((value) => {
            this._topUpRequiredAmount = value;
        });

        this.isTopUpRequired$.subscribe((isRequired) => {
            this.topUpRequiredAmountChange$.next(
                !isRequired
                    ? this._minimumTopUpAmount
                    : this._listingCost - this._memberBalance
            );
            return isRequired;
        });
    }

    ngOnDestroy(): void {
        this._destroyed$.next();
    }

    getListingCost(): Observable<number> {
        return of(this._listingCost);
    }

    getTopUpRequiredAmount(): number {
        return this._topUpRequiredAmount;
    }

    private setAmountToTopUp(): void {
        this.isTopUpRequired$.next(this._memberBalance - this._listingCost < 0);
    }
}
